// Темний лівий сайдбар (SPEC розд. 11, D33): пункти з nav.ts, приховування за правами, розкривна група адміністрування.
// На екранах < lg той самий вміст показується у висувній панелі (бургер у топбарі).
import { useQuery } from '@tanstack/react-query';
import { CalendarRange, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { api, type Paged } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/cn';
import { visibleNav, type NavBadgeSource, type NavItem } from './nav';
import { useSemester } from './semester';

interface UniversityBrief {
  id: string;
  name: string;
  shortName?: string | null;
}

/** Назва університету з довідника (D33: без логотипа — лише назва). */
function useUniversityName(enabled: boolean): string | null {
  const q = useQuery({
    queryKey: ['directories', 'university', 'items', 'brand'],
    queryFn: () => api.get<Paged<UniversityBrief>>('/directories/university/items', { pageSize: 1 }),
    enabled,
    staleTime: 30 * 60_000,
    retry: false,
  });
  const u = q.data?.items?.[0];
  return u ? u.shortName || u.name : null;
}

/** Лічильник пункту меню (напр. вхідні погодження U13); 0 або без права — нічого. */
function NavBadge({ source }: { source: NavBadgeSource }) {
  const { can } = useAuth();
  const enabled = !source.permission || can(source.permission);
  const q = useQuery({
    queryKey: source.queryKey,
    queryFn: () => api.get<{ count: number }>(source.path),
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: false,
  });
  const n = enabled ? (q.data?.count ?? 0) : 0;
  if (!n) return null;
  return (
    <span className="num ml-auto flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-danger px-1 text-[10px] leading-none font-semibold text-on-danger" aria-label={`${n} очікують`}>
      {n > 99 ? '99+' : n}
    </span>
  );
}

function itemClass(active: boolean) {
  return cn(
    'flex h-9 w-full items-center gap-3 rounded-field px-3 text-[14px] font-medium transition-colors',
    active ? 'bg-nav-active text-nav-fg' : 'text-nav-muted hover:bg-nav-hover hover:text-nav-fg',
  );
}

function NavGroup({ item, search, onNavigate }: { item: NavItem; search: string; onNavigate?: () => void }) {
  const { pathname } = useLocation();
  const inside = pathname === item.to || pathname.startsWith(`${item.to}/`);
  const [open, setOpen] = useState(inside);
  const Icon = item.icon;
  const expanded = open || inside;
  return (
    <li>
      <button type="button" className={itemClass(false)} aria-expanded={expanded} onClick={() => setOpen(!expanded)}>
        <Icon className="size-[18px] shrink-0" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-left">{item.label}</span>
        <ChevronDown className={cn('size-4 shrink-0 transition-transform', expanded && 'rotate-180')} aria-hidden />
      </button>
      {expanded ? (
        <ul className="mt-0.5 ml-[21px] flex flex-col gap-0.5 border-l border-nav-divider pl-3">
          {item.children?.map((c) => (
            <li key={c.id}>
              <NavLink
                to={{ pathname: c.to, search }}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'flex h-8 items-center rounded-[10px] px-2.5 text-[13px] font-medium transition-colors',
                    isActive ? 'bg-nav-chip text-nav-fg' : 'text-nav-muted hover:bg-nav-hover hover:text-nav-fg',
                  )
                }
              >
                {c.label}
              </NavLink>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const { can, user } = useAuth();
  const { search } = useSemester();
  const items = visibleNav(can, user);
  const universityName = useUniversityName(can('directories.view'));
  const main = items.filter((i) => i.section === 'main');
  const admin = items.filter((i) => i.section === 'admin');

  const renderItem = (item: NavItem) => {
    if (item.children) return <NavGroup key={item.id} item={item} search={search} onNavigate={onNavigate} />;
    const Icon = item.icon;
    return (
      <li key={item.id}>
        <NavLink to={{ pathname: item.to, search }} end={item.end} onClick={onNavigate} className={({ isActive }) => itemClass(isActive)}>
          <Icon className="size-[18px] shrink-0" aria-hidden />
          <span className="truncate">{item.label}</span>
          {item.badge ? <NavBadge source={item.badge} /> : null}
        </NavLink>
      </li>
    );
  };

  return (
    <div className="flex h-full flex-col bg-nav text-nav-fg">
      <div className={cn('flex items-center gap-2.5 px-4 pt-4 pb-3', onNavigate && 'pr-12')}>
        <span className="flex size-9 shrink-0 items-center justify-center rounded-field bg-primary-solid text-on-primary-solid" aria-hidden>
          <CalendarRange className="size-5" />
        </span>
        <div className="min-w-0">
          <p className="text-[15px] leading-5 font-semibold">Розклад</p>
          <p className="truncate text-[12px] leading-4 text-nav-faint" title={universityName ?? undefined}>
            {universityName ?? 'Навчальний процес'}
          </p>
        </div>
      </div>
      <nav aria-label="Головне меню" className="nav-scroll min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        <ul className="flex flex-col gap-0.5">{main.map(renderItem)}</ul>
        {admin.length ? (
          <>
            <div className="mx-3 my-3 border-t border-nav-divider" />
            <ul className="flex flex-col gap-0.5">{admin.map(renderItem)}</ul>
          </>
        ) : null}
      </nav>
    </div>
  );
}

/** Постійний сайдбар для широких екранів. */
export function Sidebar() {
  return (
    <aside className="sticky top-0 hidden h-dvh w-[248px] shrink-0 border-r border-nav-divider lg:block">
      <SidebarContent />
    </aside>
  );
}
